import { ISearchService } from '@agent/core/search/search.service';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { NG1_SEARCH } from '@commons/core/core.provider.ajs';
import { IVisioAppointmentDetailLight, IVisioAppointmentTransfer } from '@commons/models/appointment';
import { BehaviorSubject, combineLatest, map, Observable, Subject, tap } from 'rxjs';

import { AppointmentTargetType, Group, intersect, Logger, ProfileService, Skill, Team } from '@contact/upgrade';

import { AppointmentService } from '../../appointment.service';
import { CsrFilter } from '../../transfer/form/transfer-form.component';

@Component({
  selector: 'cng-appointment-transfer-dialog',
  templateUrl: './appointment-transfer-dialog.component.html',
  styleUrls: ['./appointment-transfer-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentTransferDialogComponent implements OnInit, OnDestroy {
  public availableSkills$: Observable<Skill[]>;
  public appointment$: Observable<IVisioAppointmentDetailLight>;
  public availableTeams$: Observable<Team[]>;
  public availableGroups$: Observable<Group[]>;
  public readonly availableCsr$: Subject<any[]> = new Subject();
  private readonly teamUuidFilter$: Subject<string[]> = new BehaviorSubject([]);
  private readonly groupUuidFilter$: Subject<string[]> = new BehaviorSubject([]);

  public appointment: IVisioAppointmentTransfer;
  public isFormValid = false;

  public formDialog: FormGroup;
  public comment: FormControl;

  constructor(
    private readonly logger: Logger,
    private readonly appointmentService: AppointmentService,
    private readonly dialogRef: MatDialogRef<AppointmentTransferDialogComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly profileService: ProfileService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    @Inject(NG1_SEARCH) private readonly search: ISearchService,
    @Inject(MAT_DIALOG_DATA) private readonly data: any
  ) {
    this.logger = this.logger.forSource('AppointmentTransferDialogComponent');
    this.formDialog = this.formBuilder.group({
      comment: ['', Validators.maxLength(255)],
    });
  }

  private sortUpperCase(a, b): number {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  }

  ngOnDestroy(): void {
    this.availableCsr$.complete();
    this.teamUuidFilter$.complete();
    this.groupUuidFilter$.complete();
  }

  ngOnInit(): void {
    // read the modal parameter
    const appointmentId: string = this.data;
    if (!!appointmentId) {
      this.appointment$ = this.appointmentService.getAppointmentLight(appointmentId).pipe(
        tap((iappointment: IVisioAppointmentDetailLight) => {
          this.formDialog.patchValue({
            comment: iappointment.comment || '',
          });
          this.appointment = iappointment;
          this.appointment.skillList = [];
          this.appointment.targets = [];
          this.appointment.csr.uuid = '';
        })
      );
      this.availableSkills$ = this.appointmentService.getVisioSkills();
      // groups preparation
      this.availableGroups$ = combineLatest([this.profileService.getDomainGroups(), this.groupUuidFilter$]).pipe(
        map(([groups, ids]) => (ids.length > 0 && intersect(groups, ids, ({ id }) => id)) || groups)
      );
      // teams preparation
      this.availableTeams$ = combineLatest([this.profileService.getDomainTeams(), this.teamUuidFilter$]).pipe(
        map(([teams, ids]) => (ids.length > 0 && intersect(teams, ids, ({ id }) => id)) || teams)
      );
    } else {
      this.logger.error('[AppointmentTransferDialogComponent] missing compulsory modal parameter');
    }
  }

  submitForm(): void {
    // close the dialog with the modified appointment in parametres
    this.appointment.comment = this.formDialog.controls['comment'].value || '';
    this.dialogRef.close(this.appointment);
  }

  setAppointmentCsr(_idCsr: string, _loginCsr: string, _firstnameCsr: string, _lastnameCsr: string) {
    if (!!this.appointment) {
      this.appointment.csr.uuid = _idCsr || '';
      this.appointment.csr.login = _loginCsr || '';
      this.appointment.csr.firstName = _firstnameCsr || '';
      this.appointment.csr.lastName = _lastnameCsr || '';
    }
  }

  updateCandidateCsr(payload: CsrFilter) {
    const csr = (payload.csrPrefix && payload.csrPrefix.toLowerCase()) || '';
    const skills = payload.skillIds || [];
    if (!!skills && skills.length > 0) {
      this.search.searchCsr(payload.teamId || '', payload.groupId || '', skills, csr).then((csrList: any) => {
        const candidateCsrs = csrList.map(c => ({
          name: `${c.lastName} ${c.firstName}`,
          email: c.mail,
          uuid: c.uuid,
          team: c.team,
          groups: c.groups,
        }));
        this.availableCsr$.next(candidateCsrs.sort((a, b) => this.sortUpperCase(a, b)));

        if (payload.teamId === null && payload.groupId === null) {
          this.filterGroupsAndTeamsBySkills(candidateCsrs);
        }
      });
    } else {
      this.availableCsr$.next([]);
    }
    this.isFormValid = this.checkTargetIsValid();
  }

  onTargetSelected(
    target: { skills: Skill[] | undefined; team: Team | undefined; group: Group | undefined; csr: any | any[] | undefined } | undefined
  ) {
    if (!!this.appointment && !!target) {
      this.appointment.skillList = !!target.skills ? target.skills : [];
      this.appointment.targets = [];
      if (!!target.csr && !!target.csr.uuid) {
        this.setAppointmentCsr(target.csr.uuid, target.csr.login, target.csr.firstName, target.csr.lastName);
      } else {
        this.setAppointmentCsr('', '', '', '');
      }
      if (!!target.team) {
        this.appointment.targets.push({
          uuid: target.team.id,
          name: target.team.name,
          type: AppointmentTargetType.TEAM,
        });
      }
      if (!!target.group) {
        this.appointment.targets.push({
          uuid: target.group.id,
          name: target.group.name,
          type: AppointmentTargetType.GROUP,
        });
      }
    }
    this.isFormValid = this.checkTargetIsValid();
  }

  private filterGroupsAndTeamsBySkills(candidateCsrs: any[]) {
    const candidateTeamUuids: string[] = [];
    const candidateGroupUuids: string[] = [];
    for (const _csr of candidateCsrs) {
      if (!!_csr.team && !!_csr.team.uuid && candidateTeamUuids.indexOf(_csr.team.uuid) < 0) {
        candidateTeamUuids.push(_csr.team.uuid);
      }
      if (!!_csr.groups && _csr.groups.length > 0) {
        for (const g of _csr.groups) {
          if (!!g.uuid && candidateGroupUuids.indexOf(g.uuid) < 0) {
            candidateGroupUuids.push(g.uuid);
          }
        }
      }
    }
    this.teamUuidFilter$.next(candidateTeamUuids);
    this.groupUuidFilter$.next(candidateGroupUuids);
  }

  checkTargetIsValid(): boolean {
    if (!this.appointment) return false;
    if ((!!this.appointment.csr && !!this.appointment.csr.uuid) || (!!this.appointment.skillList && this.appointment.skillList.length > 0))
      return true;
    return false;
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
